import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, BookOpen, Calendar, FileText, Search, Tag } from "lucide-react";
import Navigation from "../components/Navigation";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TopicPage({ user }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setData(null);
    (async () => {
      try {
        const resp = await axios.get(`${API}/topics/${slug}`);
        setData(resp.data);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (!data) return;
    const name = data.topic.name;
    document.title = `${name.charAt(0).toUpperCase() + name.slice(1)} — Research Documents & Books | The Archive`;
    const desc = `Explore ${data.total} books, articles, theses, and technical reports about ${name} in The Archive — the 2RA-Edition digital library for engineering, metallurgy, mining, and scientific research.`;
    document.querySelector('meta[name="description"]')?.setAttribute("content", desc);
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50">
        <Navigation user={user} />
        <div className="text-center py-24 text-slate-500">Loading topic…</div>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-sky-50">
        <Navigation user={user} />
        <main className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="topic-not-found">
          <h1 className="text-3xl font-semibold text-slate-950">Topic not found</h1>
          <p className="mt-3 text-slate-500">This topic is not in our current directory.</p>
          <Button asChild className="mt-6 bg-slate-900 hover:bg-slate-800"><Link to="/topics">Browse all topics</Link></Button>
        </main>
      </div>
    );
  }

  const { topic, items, related, total } = data;
  const displayName = topic.name.charAt(0).toUpperCase() + topic.name.slice(1);

  return (
    <div className="min-h-screen bg-sky-50">
      <Navigation user={user} />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="topic-page">
        <Button variant="ghost" className="mb-4 text-slate-500" onClick={() => navigate("/topics")} data-testid="topic-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />All topics
        </Button>

        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-orange-600 mb-3">
          <Tag className="w-4 h-4" /> Research Topic
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 capitalize" style={{ fontFamily: "Fraunces, serif" }}>
          {displayName}
        </h1>
        <p className="mt-4 max-w-3xl text-slate-600 leading-7">
          Browse {total} curated document{total === 1 ? "" : "s"} about <strong>{topic.name}</strong> in The Archive —
          the 2RA-Edition digital library for engineering and scientific research. This collection of books, journal
          articles, theses, reports, and technical documents on {topic.name} spans extractive metallurgy, mining
          engineering, chemistry, and applied science. Every document is searchable in full text; sign in to read,
          download, ask AI questions about any document, or generate cited research drafts with the AI Researcher.
        </p>

        <div className="mt-5 flex flex-wrap gap-3">
          <Button asChild size="sm" variant="outline" data-testid="topic-search-more-btn">
            <Link to={`/smart-search?q=${encodeURIComponent(topic.name)}`}>
              <Search className="w-4 h-4 mr-1.5" />Search "{topic.name}" in full text
            </Link>
          </Button>
        </div>

        <section className="mt-8 space-y-3" data-testid="topic-items">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500">No documents currently tagged with this topic.</p>
            </div>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                className="bg-white border border-slate-200 rounded-lg p-5 hover:border-orange-300 transition-all cursor-pointer group"
                onClick={() => navigate(`/items/${item.id}`)}
                data-testid={`topic-item-${item.id}`}
              >
                <h2 className="font-semibold text-slate-900 group-hover:text-orange-600 transition-colors" style={{ fontFamily: "Fraunces, serif" }}>
                  {item.title}
                </h2>
                {item.authors?.length > 0 && <p className="text-sm text-slate-500 mt-0.5">{item.authors.join(", ")}</p>}
                {item.abstract && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{item.abstract}</p>}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {item.year && <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded"><Calendar className="w-3 h-3" />{item.year}</span>}
                  {item.language && <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">{item.language}</span>}
                  {item.file_type && <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded"><FileText className="w-3 h-3" />{item.file_type}</span>}
                  {item.price > 0 && <span className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded font-medium">${item.price.toFixed(2)}</span>}
                </div>
              </div>
            ))
          )}
        </section>

        {related?.length > 0 && (
          <section className="mt-10">
            <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: "Fraunces, serif" }}>Related topics</h2>
            <div className="flex flex-wrap gap-2" data-testid="topic-related">
              {related.map((t) => (
                <Link
                  key={t.slug}
                  to={`/topics/${t.slug}`}
                  className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5 text-sm text-orange-800 hover:bg-orange-100 capitalize"
                >
                  {t.name} <span className="text-orange-500">({t.count})</span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
